import React from 'react';
import './Apex30PercentRule.css';
import PageHeader from '../components/PageHeader';
import PropFirmRulesTable from '../components/propFirms/PropFirmRulesTable';
import { useBrandIdentity } from '../utils/brandIdentity';

function Apex30PercentRule({ onNavigate }) {
  const { appName } = useBrandIdentity();
  return (
    <>
      <PageHeader onNavigate={onNavigate} />
      <div className="apex-30-percent-page">
      <div className="apex-30-percent-container">
        <h1 className="page-title">Consistency Rules by Prop Firm</h1>
        <p className="page-intro">
          Prop firms use different consistency percentages and payout requirements. {appName} lets you select the rule
          percentage that matches your account so calculations stay aligned with your provider.
        </p>
        
        <section className="rule-section">
          <h2 className="section-title">Current Rules by Firm</h2>
          <div className="section-content">
            <p>
              Checked against each firm&apos;s own help pages and re-checked every week. When a firm changes its rules, the
              update is reviewed before it shows here. Pick the same firm and plan as a <strong>Prop firm preset</strong> in
              Blowout Risk or % Rule Risk to fill in these numbers automatically.
            </p>
            <PropFirmRulesTable />
          </div>
        </section>

        <section className="rule-section">
          <h2 className="section-title">Quick Comparison</h2>
          <div className="section-content">
            <ul>
              <li><strong>Apex Trader Funding:</strong> no consistency rule in the evaluation; 50% on funded (PA) payouts. Older (legacy) accounts may still follow the 30% windfall rule.</li>
              <li><strong>Topstep:</strong> 55% in the Trading Combine (best day under 55% of the profit target).</li>
              <li><strong>Bulenox:</strong> 40% on Master account payouts; none in Qualification.</li>
              <li><strong>Take Profit Trader:</strong> 50% in the Test; none on PRO accounts.</li>
              <li><strong>MyFundedFutures:</strong> Rapid 50%, Rapid EOD 30% and Pro 50% in the evaluation; Builder 50% per payout cycle.</li>
            </ul>
          </div>
        </section>

        <section className="rule-section">
          <h2 className="section-title">How Consistency Math Works</h2>
          <div className="section-content">
            <p>
              Most firms use a percentage rule to limit how concentrated your performance can be in one day.
              {appName} models this with two related checks: a <strong>loss-side consistency limit</strong> and a
              <strong> payout-path consistency check</strong>.
            </p>

            <h3>1) Consistency Limit (Loss Side)</h3>
            <p>
              {appName} first determines a base amount, then multiplies by your selected consistency percentage.
            </p>
            <div className="formula-box">
              <div className="formula">
                <strong>Base Amount = max(Start-of-Day Profit, Safety Net)</strong><br />
                <strong>Consistency Limit = Base Amount × Selected %</strong>
              </div>
            </div>
            <p>
              This gives the maximum adverse excursion allowed for the selected rule. A strategy is flagged if
              its historical worst loss exceeds that consistency limit.
            </p>

            <h3>2) Fastest Payout Path (Windfall / Consistency Side)</h3>
            <p>
              {appName} also checks whether one very large day would force a higher total profit before payout.
            </p>
            <div className="formula-box">
              <div className="formula">
                <strong>Largest Safe Day = Min Payout Target × Selected %</strong><br />
                <strong>Min Total Profit Required = Highest Profit Day ÷ Selected Ratio</strong>
              </div>
            </div>
            <p>
              Example: with a $2,600 target and 40% rule, largest safe day is $1,040. If highest day is above that,
              payout may still be possible, but only after more total profit is accumulated.
            </p>

            <div className="example-box">
              <h3>Simple End-to-End Example</h3>
              <ul>
                <li><strong>Selected rule:</strong> 40%</li>
                <li><strong>Start-of-Day Profit:</strong> $2,000</li>
                <li><strong>Safety Net:</strong> $2,500</li>
                <li><strong>Base Amount:</strong> max($2,000, $2,500) = $2,500</li>
                <li><strong>Consistency Limit:</strong> $2,500 × 0.40 = $1,000</li>
                <li><strong>Largest Safe Day (fastest payout path):</strong> $2,600 × 0.40 = $1,040</li>
              </ul>
              <p className="note">
                If your strategy's worst historical loss is above $1,000, it is a consistency-rule risk. If your highest profit day is
                above $1,040, the payout path may be delayed until total profit increases enough.
              </p>
            </div>
          </div>
        </section>

        <section className="rule-section">
          <h2 className="section-title">How to Use This in {appName}</h2>
          <div className="section-content">
            <div className="formula-box">
              <div className="formula">
                <strong>Pick % Rule = 20% / 30% / 40% / 50% / 55% / 60%</strong>
              </div>
            </div>
            <p>
              Set your default on the Account page, then override it in analysis screens if needed — or pick a prop firm preset and the right percentage is set for you. {appName} applies that
              percentage to consistency-limit and windfall-style consistency checks so the results match your account type.
            </p>
            <ul>
              <li><strong>% Rule Risk:</strong> checks if historical losses exceed your consistency limit.</li>
              <li><strong>Bulk Risk / CSV Upload:</strong> applies the same selected percentage across accounts.</li>
              <li><strong>Portfolio Simulator:</strong> uses the same selected percentage in risk parameter calculations.</li>
            </ul>
          </div>
        </section>

        <section className="rule-section">
          <h2 className="section-title">Important Reminder</h2>
          <div className="section-content">
            <p>
              Firm rules can change. Always verify requirements directly in your provider's documentation before
              requesting payouts or adjusting risk settings.
            </p>
            <p className="note">
              <strong>Note:</strong> {appName} is provider-agnostic and does not replace official firm policy language.
            </p>
          </div>
        </section>
      </div>
    </div>
    </>
  );
}

export default Apex30PercentRule;

